import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Shared keyboard-navigation hook for inline search dropdowns.
 *
 * Manages a `highlight` index (0-based) over a list of `count` items and
 * handles ArrowUp/ArrowDown/Enter/Escape key events so every search box in
 * the app gets identical behaviour:
 *
 *   - ArrowDown / ArrowUp  → move highlight, clamped to [0, count-1]
 *   - Enter                → call `onSelect(highlight)` and return focus to
 *                            the search input (so the user can keep typing)
 *   - Escape               → close the dropdown (highlight reset to 0)
 *
 * The hook also auto-scrolls the highlighted item into view inside the
 * dropdown's scroll container using `scrollIntoView({ block: "nearest" })`.
 *
 * Usage:
 *   const inputRef = useRef<HTMLInputElement>(null);
 *   const { highlight, highlightRef, onKeyDown } = useDropdownNav({
 *     count: matches.length,
 *     onSelect: (i) => addTest(matches[i]),
 *     inputRef,
 *   });
 *
 * Render each dropdown item with `ref={i === highlight ? highlightRef : undefined}`
 * and add `onKeyDown={onKeyDown}` to the <Input>.
 */
interface UseDropdownNavOptions {
  /** Number of items in the dropdown list (0 = empty/closed). */
  count: number;
  /** Called when Enter is pressed with a valid highlight index. */
  onSelect: (index: number) => void;
  /** Ref to the search <Input> — focus is returned here after Enter. */
  inputRef: React.RefObject<HTMLInputElement | null>;
  /** Called when Escape is pressed (optional — usually closes the dropdown). */
  onEscape?: () => void;
}

export function useDropdownNav({ count, onSelect, inputRef, onEscape }: UseDropdownNavOptions) {
  const [highlight, setHighlight] = useState(0);
  const highlightRef = useRef<HTMLButtonElement | null>(null);

  // Reset highlight to 0 whenever the list shrinks (new search, fewer results).
  useEffect(() => {
    setHighlight((h) => (h >= count ? 0 : h));
  }, [count]);

  // Scroll the highlighted item into view whenever highlight changes.
  useEffect(() => {
    highlightRef.current?.scrollIntoView({ block: "nearest" });
  }, [highlight]);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        if (count > 0) setHighlight((h) => Math.min(h + 1, count - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        if (count > 0) setHighlight((h) => Math.max(h - 1, 0));
      } else if (e.key === "Enter") {
        e.preventDefault();
        if (count > 0) {
          onSelect(highlight);
          // Return focus to the search input so the user can keep typing.
          inputRef.current?.focus();
        }
      } else if (e.key === "Escape") {
        e.preventDefault();
        setHighlight(0);
        onEscape?.();
      }
    },
    [count, highlight, onSelect, inputRef, onEscape],
  );

  return { highlight, highlightRef, onKeyDown, setHighlight };
}
